import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles, creators, heroSlides, featureVideos, faqs } from '@/mocks/data';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '蜜桃视频',
  url: 'https://mitao-video.com',
  description: '专注甜蜜生活方式的创意短视频平台，汇聚美食、旅行、时尚、萌宠等多元内容。',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6666',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressRegion: '朝阳区',
    streetAddress: '望京SOHO T1 12层',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '蜜桃视频',
  url: 'https://mitao-video.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://mitao-video.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const latestArticles = articles.slice(0, 4);

  return (
    <>
      <SEOHead
        title="甜蜜生活记录与创意短视频平台"
        description="蜜桃视频是专注甜蜜生活方式的创意短视频平台，汇聚美食、旅行、时尚、萌宠等多元内容，让每一帧都充满桃气与活力。加入蜜桃视频，记录你的精彩瞬间。"
        keywords="蜜桃视频,蜜桃视频创作者,蜜桃短视频平台,蜜桃视频美食内容,蜜桃视频旅行vlog,蜜桃视频下载,蜜桃视频热门推荐"
        schemaData={[orgSchema, websiteSchema, faqSchema]}
      />

      {/* Hero Section */}
      <section className="relative h-[520px] md:h-[680px] w-full overflow-hidden">
        <img
          src={heroSlides[0].image}
          alt="蜜桃视频 - 甜蜜生活记录与创意短视频平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/35"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="w-full max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold font-heading text-background-50 mb-4 leading-tight">
              甜蜜生活，从蜜桃开始
            </h1>
            <p className="text-lg md:text-xl text-background-50/85 mb-8 leading-relaxed">
              记录每一个值得珍藏的甜蜜瞬间，发现更多有趣创作者
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/meishi"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                立即探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full text-base font-medium hover:bg-background-50/25 transition-all whitespace-nowrap cursor-pointer"
              >
                成为创作者
              </Link>
            </div>
            <div className="flex items-center justify-center gap-8 mt-10">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">1000万+</p>
                <p className="text-xs text-background-50/70 mt-1">活跃用户</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">5万+</p>
                <p className="text-xs text-background-50/70 mt-1">优质创作者</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">50万+</p>
                <p className="text-xs text-background-50/70 mt-1">甜蜜内容</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              探索内容分类
            </h2>
            <p className="text-foreground-600 text-base">
              发现你感兴趣的甜蜜世界
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="group relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={`蜜桃视频${cat.name}内容`}
                  title={`蜜桃视频${cat.name}内容 - 甜蜜生活创意短视频`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="w-10 h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <i className={`${cat.icon} text-background-50 text-lg`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-background-50 mb-1">{cat.name}</h3>
                  <p className="text-xs text-background-50/75">{cat.count.toLocaleString()} 条内容</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Recommendations */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                热门推荐
              </h2>
              <p className="text-foreground-600 text-base">大家都在看的甜蜜内容</p>
            </div>
            <Link to="/category/meishi" className="hidden sm:flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featureVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.slug}`}
                className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`蜜桃视频 - ${video.title}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-3">{video.author}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {video.views.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i> {video.likes.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link to="/category/meishi" className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium cursor-pointer">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Creators */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              精选创作者
            </h2>
            <p className="text-foreground-600 text-base">
              关注这些有趣的灵魂，发现更多甜蜜内容
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {creators.map((creator) => (
              <div key={creator.id} className="text-center group">
                <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto mb-3">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    title={`蜜桃视频创作者 - ${creator.name}`}
                    className="w-full h-full rounded-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                    <i className="ri-verified-badge-fill text-background-50 text-xs"></i>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">{creator.name}</h3>
                <p className="text-xs text-foreground-500 mb-2">
                  {creator.followers >= 10000
                    ? `${(creator.followers / 10000).toFixed(0)}万粉丝`
                    : `${creator.followers.toLocaleString()}粉丝`}
                </p>
                <p className="text-xs text-foreground-400 line-clamp-2 mb-3 hidden md:block">
                  {creator.description}
                </p>
                <button className="px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer">
                  关注
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                最新内容
              </h2>
              <p className="text-foreground-600 text-base">发现新鲜出炉的甜蜜故事</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {latestArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`蜜桃视频 - ${article.title}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                    {article.categoryName}
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.author}</span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 px-4 md:px-6 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=soft%20dreamy%20abstract%20background%20with%20gentle%20peach%20and%20cream%20gradients%2C%20floating%20delicate%20petals%2C%20warm%20ethereal%20atmosphere%2C%20minimalist%20artistic%20composition%2C%20light%20airy%20texture%2C%20soft%20bokeh%20effects&width=1800&height=600&seq=cta-home-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/85 to-primary-500/75"></div>
        <div className="relative max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-background-50 mb-4">
            加入蜜桃视频，开启甜蜜创作之旅
          </h2>
          <p className="text-background-50/80 text-base md:text-lg mb-8">
            无论你是美食达人、旅行爱好者、时尚博主还是萌宠家长，蜜桃视频都欢迎你的加入。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/about"
              className="px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-medium hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
            >
              立即加入
            </Link>
            <Link
              to="/category/meishi"
              className="px-8 py-3.5 bg-transparent text-background-50 border border-background-50/40 rounded-full text-base font-medium hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
            >
              浏览内容
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-600 text-base">
              关于蜜桃视频，你想知道的都在这里
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
                <summary className="flex items-center justify-between text-foreground-900 font-medium text-sm list-none">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}